import { apiFetch } from './apiClient.js';
import { config } from '../config.js';
import { authorizationHeader } from '../auth/authClient.js';

/** #155 — Cliente HTTP del reporting-api. */

export interface ReportDefinition {
  id: string;
  name: string;
  description: string;
  fields: string[];
}

export interface ReportCatalog {
  items: ReportDefinition[];
}

export interface DashboardMetrics {
  msgs_received: number;
  flows_completed: number;
  handoffs_requested: number;
  outbound_sent: number;
  outbound_failed: number;
  surveys_started: number;
  surveys_completed: number;
  nps_avg: number | null;
  queueNow: number;
  activeNow: number;
}

export interface DashboardChat {
  conversationId: string;
  channel: string;
  status: 'active' | 'completed' | 'failed' | 'queued';
  flowId: string;
  currentStepId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardSnapshot {
  generatedAt: string;
  tenantId: string;
  metrics: DashboardMetrics;
  activeChats: DashboardChat[];
  queueChats: DashboardChat[];
  wordCloud: Array<{ word: string; count: number }>;
  note?: string;
}

export async function getReportCatalog(): Promise<ReportCatalog> {
  return apiFetch<ReportCatalog>('/reports/catalog', { service: 'reporting' });
}

export async function getDashboardKpis(limit: number = 25): Promise<DashboardSnapshot> {
  return apiFetch<DashboardSnapshot>(`/dashboard/kpis?limit=${limit}`, { service: 'reporting' });
}

export interface ReportQueryParams {
  id: string;
  from?: string;
  to?: string;
  limit?: number;
}

export interface ReportQueryResult {
  report: { id: string; name: string; fields: string[] };
  rows: Array<Record<string, unknown>>;
  params: ReportQueryParams;
}

export async function runReportQuery(params: ReportQueryParams): Promise<ReportQueryResult> {
  return apiFetch<ReportQueryResult>('/reports/query', {
    method: 'POST',
    body: params,
    service: 'reporting',
  });
}

export function buildReportExportUrl(params: ReportQueryParams): string {
  // Reservado: si necesitamos construir URL para download de terceros.
  const qs = new URLSearchParams();
  qs.set('id', params.id);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  return `/reports/export?${qs.toString()}`;
}

/**
 * #158 — Descarga el CSV del reporte con auth Bearer.
 * Necesario porque el endpoint usa el mismo auth que las APIs JSON
 * pero responde con Content-Type text/csv — no podemos usar apiFetch
 * (que asume JSON). Hacemos fetch directo con auth header.
 */
export async function downloadReportCsv(params: ReportQueryParams): Promise<{ blob: Blob; filename: string }> {
  const qs = new URLSearchParams();
  qs.set('id', params.id);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  const url = `${config.apiBaseUrlReporting}/reports/export?${qs.toString()}`;
  const auth = authorizationHeader();
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      accept: 'text/csv',
      ...(auth ? { authorization: auth } : {}),
    },
    credentials: 'omit',
  });
  if (!res.ok) {
    throw new Error(`Export failed (${res.status}): ${await res.text().catch(() => res.statusText)}`);
  }
  const blob = await res.blob();
  // Si el server manda Content-Disposition lo usamos; sino derivamos.
  const disposition = res.headers.get('content-disposition') ?? '';
  const m = disposition.match(/filename="?([^"]+)"?/);
  const filename = m?.[1] ?? `${params.id}-${new Date().toISOString().slice(0, 10)}.csv`;
  return { blob, filename };
}
